import React, { useState, useEffect } from "react";
import {
  Form,
  Radio,
  Slider,
  Space,
  Card,
  Typography,
  Spin,
  Progress,
  Row,
  Col,
  Badge,
} from "antd";
import StepHeader from "../../../Evaluation/StepHeader";
import { usePermissions } from "../../../../PermissionsProvider";
import { IntentService } from "../../../../services/IntentService";
import { ApplicationService } from "../../../../services/ApplicationService";
import { DatasetData } from "../../../../model_gen/dataset";
import {
  DatabaseOutlined,
  PercentageOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import { DatasetService } from "../../../../services/DatasetService";

const { Title, Text } = Typography;

interface DatasetSamplingProps {
  baseType?: "intent_group" | "application_intent";
  intentGroupId?: number;
  applicationId?: number;
  intentId?: number;
  sampleType?: "all" | "random" | "none";
  samplePercentage?: number;
  dateStart?: number;
  dateEnd?: number;
  ignoredApplicationIds?: number[];
  onChange: (data: Partial<DatasetData>) => void;
}

const DatasetSampling: React.FC<DatasetSamplingProps> = ({
  baseType,
  intentGroupId,
  applicationId,
  intentId,
  sampleType = "all",
  samplePercentage = 100,
  ignoredApplicationIds = [],
  onChange,
}) => {
  const { apiKey } = usePermissions();
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRequests, setTotalRequests] = useState<number>(0);
  const [baseLabel, setBaseLabel] = useState<string>("");

  // Fetch data when needed parameters are available
  const fetchRequestCount = () => {
    if (
      !apiKey ||
      !(
        (baseType === "intent_group" && intentGroupId) ||
        (baseType === "application_intent" && applicationId && intentId)
      )
    ) {
      return;
    }

    setLoading(true);

    // Convert intentGroupId, applicationId, and intentId to the correct format for the API
    const igId = baseType === "intent_group" ? intentGroupId : null;
    const appId = baseType === "application_intent" ? applicationId : null;
    const intId = baseType === "application_intent" ? intentId : null;

    DatasetService.getEstimatedDatasetRequestCount(
      igId,
      appId,
      intId,
      null,
      null,
      ignoredApplicationIds,
      setLoading,
      apiKey
    ).subscribe(
      (response: any) => {
        console.log("Estimated request count response:", response);
        let total = 0;
        if (response && typeof response.count === "number") {
          total = response.count;
        }
        setTotalRequests(total);
        setLoading(false);
        onChange({ total_requests: total });
      },
      (error: any) => {
        console.error("Error fetching estimated request count:", error);
        setLoading(false);
      }
    );
  };

  // Fetch data when component mounts or when key parameters change
  useEffect(() => {
    fetchRequestCount();
  }, [baseType, intentGroupId, applicationId, intentId, apiKey]);

  // Get intent group or application/intent names for display
  useEffect(() => {
    if (!apiKey) return;

    if (baseType === "intent_group" && intentGroupId) {
      IntentService.getIntentGroup(
        intentGroupId,
        false,
        setLoading,
        apiKey
      ).subscribe(
        (response: any) => {
          console.log("Intent group response:", response);
          let name = `Intent Group ${intentGroupId}`;
          if (response) {
            name = response.intent_name || response.name || name;
          }
          setBaseLabel(`Intent Group: ${name}`);
        },
        (error: any) => {
          console.error("Error fetching intent group:", error);
          setBaseLabel(`Intent Group: ${intentGroupId}`);
        }
      );
    } else if (baseType === "application_intent" && applicationId && intentId) {
      ApplicationService.getApplication(
        applicationId,
        setLoading,
        apiKey
      ).subscribe(
        (appResponse: any) => {
          console.log("Application response:", appResponse);
          const appName =
            appResponse?.application_name ||
            appResponse?.name ||
            `Application ${applicationId}`;

          ApplicationService.getApplicationAction(
            applicationId,
            intentId,
            setLoading,
            apiKey
          ).subscribe(
            (intentResponse: any) => {
              console.log("Intent response:", intentResponse);
              const intentName =
                intentResponse?.action_type || `Intent ${intentId}`;
              setBaseLabel(`Application: ${appName}, Intent: ${intentName}`);
            },
            (error: any) => {
              console.error("Error fetching intent:", error);
              setBaseLabel(`Application: ${appName}, Intent: ${intentId}`);
            }
          );
        },
        (error: any) => {
          console.error("Error fetching application:", error);
          setBaseLabel(`Application: ${applicationId}, Intent: ${intentId}`);
        }
      );
    }
  }, [baseType, intentGroupId, applicationId, intentId, apiKey]);

  const calculateSampleSize = () => {
    if (sampleType === "all") {
      return totalRequests;
    } else if (sampleType === "random" && samplePercentage) {
      return Math.round((totalRequests * samplePercentage) / 100);
    } else {
      return 0;
    }
  };

  const sampleSize = calculateSampleSize();
  const samplePercentageOfTotal =
    totalRequests > 0 ? (sampleSize / totalRequests) * 100 : 0;

  return (
    <Space
      direction="vertical"
      size="large"
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
      }}>
      <StepHeader
        title="Choose Sampling Method"
        description="Select how you want to sample data for your dataset"
        style={{ textAlign: "center" }}
      />

      <div style={{ width: "600px" }}>
        {/* Selected Base Info Card */}
        <Card style={{ marginBottom: "24px" }} loading={loading && !baseLabel}>
          <Row align="middle" gutter={16}>
            <Col>
              <DatabaseOutlined
                style={{ fontSize: "24px", color: "#1890ff" }}
              />
            </Col>
            <Col flex="auto">
              <Title level={5} style={{ margin: 0 }}>
                Selected Base
              </Title>
              <Text>{baseLabel || "Loading..."}</Text>
            </Col>
            <Col>
              <Badge
                count={totalRequests}
                overflowCount={9999}
                style={{
                  backgroundColor: "#52c41a",
                }}
                title={`${totalRequests} requests available`}
              />
            </Col>
          </Row>
        </Card>

        {/* Sampling Method Selection */}
        <Card title="Sampling Method" style={{ marginBottom: "24px" }}>
          <Form layout="vertical">
            <Form.Item
              required
              tooltip="Choose how to sample data for your dataset">
              <Radio.Group
                value={sampleType}
                onChange={(e) => onChange({ sample_type: e.target.value })}
                style={{ width: "100%" }}>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Radio value="all">
                    <Space>
                      <Text strong>Use All Requests</Text>
                      <Text type="secondary">
                        Include all available requests in the dataset
                      </Text>
                    </Space>
                  </Radio>
                  <Radio value="random">
                    <Space>
                      <Text strong>Random Sample</Text>
                      <Text type="secondary">
                        Select a random percentage of requests
                      </Text>
                    </Space>
                  </Radio>
                  <Radio value="none">
                    <Space>
                      <Text strong>None (Empty Dataset)</Text>
                      <Text type="secondary">
                        Create an empty dataset with no requests
                      </Text>
                    </Space>
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>

            {sampleType === "random" && (
              <Form.Item
                label={
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}>
                    <span>Sample Percentage</span>
                    <span
                      style={{
                        fontWeight: "bold",
                        color: "#21b892",
                        marginLeft: "20px",
                      }}>
                      {samplePercentage}%
                    </span>
                  </div>
                }
                required
                tooltip="Percentage of requests to include in the dataset">
                <div style={{ display: "flex", alignItems: "center" }}>
                  <PercentageOutlined
                    style={{ marginRight: "10px", color: "#21b892" }}
                  />
                  <Slider
                    min={1}
                    max={100}
                    value={samplePercentage}
                    onChange={(value: number) =>
                      onChange({ sample_percentage: value })
                    }
                    style={{ flex: 1 }}
                    tooltipVisible={false}
                    trackStyle={{ backgroundColor: "#21b892" }}
                    handleStyle={{
                      borderColor: "#21b892",
                      backgroundColor: "#21b892",
                    }}
                  />
                </div>
                <div
                  style={{
                    marginTop: "8px",
                    display: "flex",
                    justifyContent: "space-between",
                  }}>
                  <Text type="secondary">1%</Text>
                  <Text type="secondary">50%</Text>
                  <Text type="secondary">100%</Text>
                </div>
              </Form.Item>
            )}
          </Form>
        </Card>

        {/* Dataset Size Preview */}
        <Card title="Dataset Size Preview">
          <Row gutter={[16, 16]} align="middle">
            <Col span={24}>
              <Progress
                percent={samplePercentageOfTotal}
                status="active"
                format={(percent) => `${Math.round(percent!)}%`}
                strokeColor={{
                  "0%": "#21b892",
                  "100%": "#FFDB6A",
                }}
              />
            </Col>
            <Col span={12}>
              <Card bordered={false} style={{ background: "#f5f5f5" }}>
                <Statistic
                  title="Total Available"
                  value={totalRequests}
                  suffix="requests"
                  loading={loading}
                  valueStyle={{ color: "#21b892" }}
                  prefix={<DatabaseOutlined />}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card bordered={false} style={{ background: "#f5f5f5" }}>
                <Statistic
                  title="Estimated Dataset Size"
                  value={sampleSize}
                  suffix="requests"
                  valueStyle={{ color: "#FFDB6A" }}
                  prefix={<InboxOutlined />}
                />
              </Card>
            </Col>
          </Row>
        </Card>
      </div>
    </Space>
  );
};

// Helper Statistic component since we can't import it directly
const Statistic = ({
  title,
  value,
  suffix,
  loading,
  valueStyle,
  prefix,
}: any) => {
  return (
    <div>
      <div
        style={{
          marginBottom: "8px",
          color: "rgba(0, 0, 0, 0.45)",
          fontSize: "14px",
        }}>
        {title}
      </div>
      {loading ? (
        <Spin size="small" />
      ) : (
        <div style={{ ...valueStyle, fontSize: "24px", fontWeight: 500 }}>
          {prefix && <span style={{ marginRight: "8px" }}>{prefix}</span>}
          {value}{" "}
          {suffix && (
            <span style={{ fontSize: "16px", color: "rgba(0, 0, 0, 0.65)" }}>
              {suffix}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default DatasetSampling;
